import { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';

import { useDeleteNamePattern } from '@/api/name-pattern/use-delete-name-pattern.ts';
import { useNamePatternDetail } from '@/api/name-pattern/use-name-pattern-detail.ts';
import type { NamePatternDtoTypeEnum, NamePatternListDto } from '@/api/types';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';

type NamePatternGroupRowProps = {
  type: NamePatternDtoTypeEnum;
  group: NamePatternListDto;
};

const NamePatternGroupRow = ({ type, group }: NamePatternGroupRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: baseNames, isLoading } = useNamePatternDetail(
    type,
    isExpanded ? group.overrideName : undefined,
  );
  const { mutate: deleteNamePattern, isPending: deletePending } = useDeleteNamePattern();

  const handleDelete = (uuid: string, baseName: string) => {
    if (confirm(`Remove "${baseName}" from "${group.overrideName}"?`)) {
      deleteNamePattern(uuid);
    }
  };

  return (
    <div className="border rounded-lg text-xs">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 p-3 text-left"
        onClick={() => setIsExpanded((current) => !current)}
      >
        <div className="flex items-center gap-2 flex-wrap">
          {isExpanded ? (
            <ChevronDown className="size-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 text-muted-foreground" />
          )}
          <span className="font-medium text-sm">{group.overrideName}</span>
          <Badge variant="outline">{group.count} base name(s)</Badge>
          {group.category && <Badge variant="info">{group.category.name}</Badge>}
        </div>
      </button>
      {isExpanded && (
        <div className="border-t px-3 py-2 flex flex-col gap-1">
          {isLoading && <span className="text-muted-foreground">Loading...</span>}
          {baseNames?.map((pattern) => (
            <div key={pattern.uuid} className="flex items-center justify-between gap-2 py-1">
              <span>{pattern.baseName}</span>
              <Button
                variant="outline"
                size="icon"
                disabled={deletePending}
                onClick={() => handleDelete(pattern.uuid, pattern.baseName)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
          {!isLoading && !baseNames?.length && (
            <span className="text-muted-foreground">No base names left</span>
          )}
        </div>
      )}
    </div>
  );
};

export default NamePatternGroupRow;
